import { emptyOwnedPastes, type OwnedPaste, type OwnedPastesState } from './ownedPastesSlice';

/** Storage key; bump the version suffix on incompatible format changes. */
export const STORAGE_KEY = 'pasta.ownedPastes.v1';

function isOwnedPaste(value: unknown): value is OwnedPaste {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return ['id', 'deleteToken', 'createdAt', 'preview'].every((key) => typeof candidate[key] === 'string');
}

/**
 * Reads owned pastes from storage. Storage can be unavailable (private mode, blocked site data) or
 * hold anything, so every failure degrades to "nothing owned" instead of breaking the app.
 */
export function loadOwnedPastes(storage: Storage | undefined): OwnedPastesState {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (raw == null) {
      return emptyOwnedPastes;
    }
    const parsed: unknown = JSON.parse(raw);
    const entries = Array.isArray(parsed) ? parsed.filter(isOwnedPaste) : [];
    // keep only known fields, so data written by older versions is normalised
    const owned = entries.map(({ id, deleteToken, createdAt, preview, title }): OwnedPaste => ({
      id,
      deleteToken,
      createdAt,
      preview,
      ...(typeof title === 'string' ? { title } : {}),
    }));
    return { byId: Object.fromEntries(owned.map((paste) => [paste.id, paste])) };
  } catch {
    return emptyOwnedPastes;
  }
}

/** Writes owned pastes; failures are ignored (the in-memory state stays authoritative). */
export function saveOwnedPastes(storage: Storage | undefined, state: OwnedPastesState): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(Object.values(state.byId)));
  } catch {
    // quota exceeded or storage blocked: tokens survive only for this session
  }
}
